import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useRestaurant } from "@/contexts/RestaurantContext";
import {
  Box,
  Typography,
  TextField,
  Grid2 as Grid,
  Container,
  Button
} from "@mui/material";
import { Search as SearchIcon, Add as PlusIcon } from "@mui/icons-material";
import { recipeService } from "@/services/api/recipes";
import type { Recipe, PaginatedRecipeResponse } from "@/types/recipe";
import ListView from "./components/ListView";

const Recipes = () => {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [totalRecipes, setTotalRecipes] = useState(0);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { restaurantProfile, isLoading: isLoadingProfile } = useRestaurant();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get state from URL parameters
  const searchQuery = searchParams.get('search') || '';
  const page = parseInt(searchParams.get('page') || '0', 10);
  const pageSize = parseInt(searchParams.get('pageSize') || '20', 10);

  useEffect(() => {
    const loadRecipes = async () => {
      if (!restaurantProfile) return;
      
      try {
        setLoading(true);
        let response;
        if (searchQuery.trim()) {
          const filteredRecipes = recipes.filter(recipe => 
            recipe.name.toLowerCase().includes(searchQuery.toLowerCase())
          );
          setRecipes(filteredRecipes);
          setTotalRecipes(filteredRecipes.length);
        } else {
          response = (await recipeService.getRecipes(
            restaurantProfile.business_unit_id,
            page * pageSize,
            pageSize
          )) as PaginatedRecipeResponse;
          setRecipes(response.recipes);
          setTotalRecipes(response.pagination.total);
        }
        // Ensure search is always local
        if (searchQuery.trim()) {
          return;
        }
      } catch (error) {
        console.error(t('recipes.errors.loading'), error);
        setRecipes([]);
        setTotalRecipes(0);
      } finally {
        setLoading(false);
      }
    };

    loadRecipes();
  }, [searchQuery, page, pageSize, restaurantProfile]);
  
  // Handle search change
  const handleSearchChange = (value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('search', value);
    } else {
      newParams.delete('search');
    }
    newParams.set('page', '0'); // Reset to first page on search
    setSearchParams(newParams);
  };
  
  // Handle pagination change
  const handlePageChange = (model: { page: number; pageSize: number }) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', model.page.toString());
    newParams.set('pageSize', model.pageSize.toString());
    setSearchParams(newParams);
  };

  const goToRecipeDetail = (recipeId: number) => {
    if (!restaurantProfile) return;
    // Preserve current search and pagination state in the URL
    const currentParams = searchParams.toString();
    const returnUrl = currentParams ? `/recipes?${currentParams}` : '/recipes';
    navigate(`/recipes/${recipeId}?businessUnitId=${restaurantProfile.business_unit_id}&returnUrl=${encodeURIComponent(returnUrl)}`);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Grid
          container
          spacing={2}
          sx={{ alignItems: "center", maxWidth: "100%", justifyContent: "space-between" }}
        >
          <Grid xs="auto">
            <Typography variant="h4" component="h1" gutterBottom={false}>
              {t("nav.recipes")}
            </Typography>
          </Grid>
          <Grid xs={true}>
            <Box sx={{ position: "relative", width: "100%" }}>
              <SearchIcon
                sx={{
                  position: "absolute",
                  left: 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "text.secondary",
                  zIndex: 1,
                }}
              />
              <TextField
                fullWidth
                placeholder={t('recipes.search')}
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                size="small"
                sx={{
                  '& .MuiInputBase-input': {
                    paddingLeft: '40px',
                  },
                  width: '100%',
                  maxWidth: '600px',
                  ml: 'auto',
                  mr: 2
                }}
              />
            </Box>
          </Grid>
          <Grid xs="auto" sx={{ flexShrink: 0 }}>
            <Button
              variant="contained"
              startIcon={<PlusIcon />}
            >
              {t('recipes.addRecipe')}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {isLoadingProfile ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <Typography>{t('recipes.loadingProfile')}</Typography>
        </Box>
      ) : !restaurantProfile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: 4 }}>
          <Typography>{t('recipes.setupProfile')}</Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate('/settings')}
            startIcon={<PlusIcon />}
          >
            {t('recipes.setupProfileButton')}
          </Button>
        </Box>
      ) : (
        <ListView
          recipes={recipes}
          onRecipeClick={goToRecipeDetail}
          totalRecipes={totalRecipes}
          loading={loading}
          onPageChange={handlePageChange}
          pageSize={pageSize}
          page={page}
        />
      )}
    </Container>
  );
};

export default Recipes;
